import { FC, useCallback } from "react";
import { Chip, Stack } from "@mui/material";

import { useMenuHook } from "@/utils/hooks/useMenu.hook";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import { useAppDispatch } from "@/store/hooks";
import { AttachmentPopover } from "@/components/pages/common/resources-ag-grid/components/column-defs/column-def-cell-renderers/attachments/AttachmentPopover";
import { Attachment, Ebs, Ec2 } from "@/services/cloudchipr.api";
import { AttachmentItemDetachedState } from "@/components/pages/common/resources-ag-grid/components/column-defs/column-def-cell-renderers/attachments/AttachmentItemDetachedState";
import { findAndSetAttachResourceDetailsDrawerThunk } from "@/store/live-usage-mgmt/thunks/findAndSetAttachResourceDetailsDrawerThunk";

interface AttachmentCellProps {
  resource: Ec2 | Ebs;
}

export const AttachmentsColumnCellRenderer: FC<AttachmentCellProps> = ({
  resource,
}) => {
  const { anchor, open, openMenu, closeMenu } = useMenuHook();
  const dispatch = useAppDispatch();
  const attachments = resource?.attachments as Attachment[];

  const popoverItemClickHandler = useCallback(
    (providerUniqueId: string) => {
      dispatch(
        findAndSetAttachResourceDetailsDrawerThunk({
          providerUniqueId,
          accountId: resource.account.id,
          resourceType: resource.resource_type,
        }),
      );
      closeMenu();
    },
    [dispatch, resource, closeMenu],
  );

  const firstLabelClickHandler = useCallback(() => {
    popoverItemClickHandler(attachments?.at(0)?.id ?? "");
  }, [attachments, popoverItemClickHandler]);

  const onlyAttachedStatuses = attachments?.filter(
    (item) => item.status === "attached",
  );

  if (!attachments) {
    return <div>-</div>;
  }

  if (attachments?.length === 0) {
    return <AttachmentItemDetachedState />;
  }

  return (
    <Stack direction="row" alignItems="center" width="100%" overflow="hidden">
      <Chip
        size="small"
        onClick={firstLabelClickHandler}
        label={
          <TypographyWithTooltip
            variant="inherit"
            title={attachments.at(0)?.id}
            color="primary"
          />
        }
        sx={{ overflow: "hidden", width: "fit-content", color: "primary" }}
        variant="outlined"
      />
      {onlyAttachedStatuses?.length > 1 && (
        <Chip
          label={`+${onlyAttachedStatuses.length - 1}`}
          onClick={openMenu}
          variant="outlined"
          size="small"
          sx={{ ml: 0.5 }}
        />
      )}

      {open && (
        <AttachmentPopover
          open={open}
          anchor={anchor}
          attachments={onlyAttachedStatuses}
          closeMenu={closeMenu}
          onClick={popoverItemClickHandler}
        />
      )}
    </Stack>
  );
};
